// Phase 22 — Unified Admin Product Management
// =============================================
// Single tabbed page that consolidates the previously-scattered admin
// product surfaces into ONE place:
//   1. All Products       — full catalog editor (live storefront)
//   2. Lifted Inventory   — native inventory editor with deep edit links
//   3. Partner Products   — approved partner-submitted products
//   4. Pending Approvals  — review/approve/reject partner submissions
//   5. Featured / Top     — toggle featured/top flag on any product
//   6. Categories         — collection management
//   7. Inventory Mgmt     — quick stock + status toggles
//
// All existing routes (/admin/products, /admin/inventory, /admin/collections,
// /admin/product-approvals, /admin/compliance) continue to work — this page
// is a unified shell that lets admins jump between them without leaving the
// product context.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  ShoppingBag, Package, Award, ImageIcon, Layers, Store,
  Loader2, CheckCircle2, XCircle, AlertTriangle, ExternalLink,
  ShieldCheck, RefreshCw, Edit3, Wrench, KeyRound, X,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ProductImg } from "@/lib/productImage";

import AdminProductsEditor from "@/pages/admin/AdminProductsEditor";
import AdminInventoryList from "@/pages/admin/AdminInventoryList";
import AdminCollections from "@/pages/admin/storefront/AdminCollections";
import ShopifyMergeButton from "@/components/admin/ShopifyMergeButton";

const TABS = [
  // Phase 22+ — Lifted Inventory is now the primary control center for all
  // LATM-owned products. Default tab.
  { id: "inventory",    label: "Lifted Inventory",  icon: Package,     testid: "tab-inventory" },
  { id: "partner",      label: "Partner Products",  icon: Store,       testid: "tab-partner" },
  { id: "approvals",    label: "Pending Approvals", icon: Award,       testid: "tab-approvals" },
  { id: "compliance",   label: "AI Compliance",     icon: ShieldCheck, testid: "tab-compliance" },
  { id: "featured",     label: "Featured / Top",    icon: ImageIcon,   testid: "tab-featured" },
  { id: "archived",     label: "Archived",          icon: AlertTriangle, testid: "tab-archived" },
  { id: "categories",   label: "Categories",        icon: Layers,      testid: "tab-categories" },
  { id: "all",          label: "All Products",      icon: ShoppingBag, testid: "tab-all" },
];

export default function ProductManagement() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "inventory";
  const [pendingCount, setPendingCount] = useState(0);

  // Phase 8 — Live pending-approval count for the badge on the Pending tab
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/admin/products/pending");
        if (alive) setPendingCount(data.count ?? (data.partner_pending || []).length);
      } catch (e) { console.warn("[admin-products] pending fetch failed", e); }
    })();
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/products/pending");
        if (alive) setPendingCount(data.count ?? (data.partner_pending || []).length);
      } catch (e) { console.warn("[admin-products] pending fetch failed", e); }
    }, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [tab]);

  const setTab = (t) => {
    const next = new URLSearchParams(sp);
    next.set("tab", t);
    setSp(next, { replace: true });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="admin-product-management">
      <header className="mb-5">
        <div className="text-xs uppercase tracking-[0.3em] text-lifted-green">LiftedAtTheMoment</div>
        <h1 className="font-anton text-3xl uppercase mt-1">Product Management</h1>
        <p className="text-sm text-lifted-dim mt-1 max-w-2xl">
          One workspace for products, inventory, partner submissions, approvals, categories, featured items and stock — replaces the scattered legacy admin links.
        </p>
      </header>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-white/10 mb-5" data-testid="product-management-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = t.id === tab;
          const showBadge = t.id === "approvals" && pendingCount > 0;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={t.testid}
              className={`relative inline-flex items-center gap-2 px-3 py-2 text-xs font-bebas tracking-[0.18em] uppercase border-b-2 -mb-px ${
                active
                  ? "border-lifted-green text-lifted-green"
                  : "border-transparent text-lifted-dim hover:text-white"
              }`}
            >
              <Icon className="h-3 w-3" /> {t.label}
              {showBadge && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center"
                      data-testid="pending-approvals-badge">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === "inventory"  && <div data-testid="tab-inventory-content"><AdminInventoryList /></div>}
      {tab === "partner"    && <PartnerProductsTab />}
      {tab === "approvals"  && <PendingApprovalsTab />}
      {tab === "compliance" && <ComplianceTab />}
      {tab === "featured"   && <FeaturedTab />}
      {tab === "archived"   && <ArchivedProductsTab />}
      {tab === "categories" && <div data-testid="tab-categories-content"><AdminCollections /></div>}
      {tab === "all"        && <div data-testid="tab-all-content"><AdminProductsEditor /></div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Partner Products tab — list of partner-submitted products (any status)
// ---------------------------------------------------------------------------
function PartnerProductsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/products/unified", {
        params: { source: "partner", limit: 200 },
      });
      const filtered = statusFilter
        ? (data.products || []).filter((p) => p.approval_status === statusFilter)
        : (data.products || []);
      setRows(filtered);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="tab-partner-content" className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[0.65rem] tracking-[0.22em] uppercase font-bebas text-lifted-dim">Status:</span>
        {["", "approved", "pending", "review_required", "rejected"].map((s) => (
          <button key={s || "all"}
            data-testid={`partner-status-${s || "all"}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-bebas tracking-[0.18em] uppercase border ${
              statusFilter === s ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim hover:text-white"
            }`}>
            {s || "All"}
          </button>
        ))}
      </div>
      {loading ? (
        <div className="py-10 text-center text-lifted-dim text-sm">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading partner products…
        </div>
      ) : err ? (
        <div className="glass-panel p-4 text-rose-300 text-sm">{err}</div>
      ) : rows.length === 0 ? (
        <div className="glass-panel p-10 text-center text-lifted-dim text-sm" data-testid="partner-empty">
          No partner products in this filter.
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Compliance</th>
                <th className="text-right px-3 py-2">Price</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.handle} className="border-b border-white/5" data-testid={`partner-row-${p.handle}`}>
                  <td className="px-3 py-2 flex items-center gap-2">
                    <ProductImg product={p} alt="" className="h-8 w-8 rounded object-cover" data-testid={`partner-row-${p.handle}-img`} />
                    <span className="truncate max-w-[260px]">{p.title}</span>
                  </td>
                  <td className="px-3 py-2"><Badge value={p.approval_status} /></td>
                  <td className="px-3 py-2"><Badge value={p.compliance_status} /></td>
                  <td className="px-3 py-2 text-right">
                    {p.price_cents != null ? `$${(p.price_cents / 100).toFixed(2)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/admin/inventory/${encodeURIComponent(p.handle)}`}
                          className="text-lifted-green hover:underline inline-flex items-center gap-1 text-xs"
                          data-testid={`partner-row-${p.handle}-open`}>
                      Open <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pending Approvals tab — partner-submitted products awaiting review
// ---------------------------------------------------------------------------
function PendingApprovalsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/products/pending");
      setRows(data.partner_pending || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (handle) => {
    setBusy((b) => ({ ...b, [handle]: "approve" }));
    try {
      await api.post(`/admin/products/${encodeURIComponent(handle)}/approve`);
      setOk(`Approved: ${handle}`);
      load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy((b) => ({ ...b, [handle]: null })); }
  };
  const reject = async (handle) => {
    const reason = window.prompt("Rejection reason (optional, shown to partner):") || "";
    setBusy((b) => ({ ...b, [handle]: "reject" }));
    try {
      await api.post(`/admin/products/${encodeURIComponent(handle)}/reject`, { reason });
      setOk(`Rejected: ${handle}`);
      load();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy((b) => ({ ...b, [handle]: null })); }
  };

  return (
    <section data-testid="tab-approvals-content" className="space-y-3">
      {ok && <div className="text-lifted-green text-xs" data-testid="approvals-ok">{ok}</div>}
      {err && <div className="text-rose-300 text-xs" data-testid="approvals-err">{err}</div>}
      {loading ? (
        <div className="py-10 text-center text-lifted-dim text-sm">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading approvals queue…
        </div>
      ) : rows.length === 0 ? (
        <div className="glass-panel p-10 text-center text-lifted-dim text-sm" data-testid="approvals-empty">
          <CheckCircle2 className="h-6 w-6 text-lifted-green mx-auto mb-2" />
          Approval queue is clear. No partner products awaiting review.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="approvals-list">
          {rows.map((p) => (
            <li key={p.handle || p.id}
                className="glass-panel p-3 flex items-center justify-between gap-3"
                data-testid={`approval-row-${p.handle || p.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <ProductImg product={p} alt="" className="h-12 w-12 rounded object-cover" data-testid={`approval-${p.handle}-img`} />
                <div className="min-w-0">
                  <div className="text-sm font-bebas tracking-[0.16em] uppercase truncate">{p.title || p.handle}</div>
                  <div className="text-[0.65rem] text-lifted-dim mt-0.5 truncate">
                    Submitted by {p.partner_user_id || "—"} · {p.created_at || ""}
                  </div>
                  {p.rejection_reason && (
                    <div className="text-[0.65rem] text-amber-300 mt-0.5">Last reason: {p.rejection_reason}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => approve(p.handle)}
                  disabled={!!busy[p.handle]}
                  className="px-3 py-1.5 rounded bg-lifted-green/20 text-lifted-green border border-lifted-green/40 text-xs font-bebas tracking-[0.16em] uppercase hover:bg-lifted-green/30 disabled:opacity-50"
                  data-testid={`approval-${p.handle}-approve`}>
                  <CheckCircle2 className="h-3 w-3 inline mr-1" />
                  {busy[p.handle] === "approve" ? "…" : "Approve"}
                </button>
                <button
                  onClick={() => reject(p.handle)}
                  disabled={!!busy[p.handle]}
                  className="px-3 py-1.5 rounded bg-rose-500/15 text-rose-200 border border-rose-500/40 text-xs font-bebas tracking-[0.16em] uppercase hover:bg-rose-500/25 disabled:opacity-50"
                  data-testid={`approval-${p.handle}-reject`}>
                  <XCircle className="h-3 w-3 inline mr-1" />
                  {busy[p.handle] === "reject" ? "…" : "Reject"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Featured / Top tab — quick toggle for featured flag
// ---------------------------------------------------------------------------
function FeaturedTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/products", { params: { limit: 200 } });
      setRows(data.products || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleFeatured = async (handle, current) => {
    try {
      await api.post(`/admin/products/${encodeURIComponent(handle)}/toggle-featured`, { featured: !current });
      load();
    } catch (e) {
      setErr(apiErrorMessage(e));
    }
  };

  if (loading) return (
    <div className="py-10 text-center text-lifted-dim text-sm" data-testid="tab-featured-content">
      <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
    </div>
  );

  const featured = rows.filter((p) => p.featured);
  const others = rows.filter((p) => !p.featured);

  return (
    <section data-testid="tab-featured-content" className="space-y-5">
      {err && <div className="text-rose-300 text-xs">{err}</div>}
      <div>
        <div className="text-xs font-bebas tracking-[0.22em] uppercase text-lifted-green mb-2">
          Currently Featured ({featured.length})
        </div>
        {featured.length === 0 ? (
          <div className="glass-panel p-5 text-center text-lifted-dim text-sm">
            <AlertTriangle className="h-4 w-4 inline mr-1" />
            No featured products. The Top Products homepage rail uses a fallback chain.
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {featured.map((p) => (
              <FeaturedRow key={p.handle} p={p} onToggle={toggleFeatured} />
            ))}
          </ul>
        )}
      </div>
      <div>
        <div className="text-xs font-bebas tracking-[0.22em] uppercase text-lifted-dim mb-2">
          Promote a product to Featured
        </div>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
          {others.slice(0, 40).map((p) => (
            <FeaturedRow key={p.handle} p={p} onToggle={toggleFeatured} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function FeaturedRow({ p, onToggle }) {
  return (
    <li className="glass-panel p-2 flex items-center justify-between gap-2"
        data-testid={`featured-row-${p.handle}`}>
      <div className="flex items-center gap-2 min-w-0">
        <ProductImg product={p} alt="" className="h-8 w-8 rounded object-cover" data-testid={`featured-row-${p.handle}-img`} />
        <span className="text-sm truncate">{p.title}</span>
      </div>
      <button
        onClick={() => onToggle(p.handle, !!p.featured)}
        className={`shrink-0 px-2 py-1 rounded text-[0.6rem] font-bebas tracking-[0.16em] uppercase ${
          p.featured ? "bg-lifted-green/20 text-lifted-green border border-lifted-green/40"
                     : "bg-white/5 text-lifted-dim border border-white/10 hover:text-white"
        }`}
        data-testid={`featured-toggle-${p.handle}`}>
        {p.featured ? "Featured" : "Make Featured"}
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Archived Products tab — list archived products with Restore action
// ---------------------------------------------------------------------------
function ArchivedProductsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/inventory/products", {
        params: { status: "archived", limit: 200 },
      });
      setRows(data.products || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (handle) => {
    try {
      await api.post(`/admin/inventory/products/${encodeURIComponent(handle)}/restore`);
      load();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  if (loading) return (
    <div className="py-10 text-center text-lifted-dim text-sm" data-testid="tab-archived-content">
      <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading archived…
    </div>
  );

  return (
    <section data-testid="tab-archived-content">
      {err && <div className="text-rose-300 text-xs mb-2">{err}</div>}
      {rows.length === 0 ? (
        <div className="glass-panel p-10 text-center text-lifted-dim text-sm" data-testid="archived-empty">
          No archived products.
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
              <tr className="border-b border-white/5">
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Archived</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.handle} className="border-b border-white/5" data-testid={`archived-row-${p.handle}`}>
                  <td className="px-3 py-2 truncate max-w-[280px]">{p.title}</td>
                  <td className="px-3 py-2 text-xs text-lifted-dim">{p.category || "—"}</td>
                  <td className="px-3 py-2 text-xs text-lifted-dim">{(p.archived_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => restore(p.handle)}
                            className="text-lifted-green hover:underline text-xs inline-flex items-center gap-1"
                            data-testid={`archived-restore-${p.handle}`}>
                      Restore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Badge subcomponent
// ---------------------------------------------------------------------------
function Badge({ value }) {
  const v = (value || "").toLowerCase();
  const color = useMemo(() => {
    if (["approved", "active", "compliant"].includes(v)) return "bg-lifted-green/15 text-lifted-green border-lifted-green/40";
    if (["pending", "review_required", "needs_review", "draft"].includes(v)) return "bg-amber-400/15 text-amber-300 border-amber-400/40";
    if (["rejected", "archived", "restricted"].includes(v)) return "bg-rose-500/15 text-rose-300 border-rose-500/40";
    return "bg-white/5 text-white/60 border-white/10";
  }, [v]);
  return (
    <span className={`px-1.5 py-0.5 rounded border text-[0.55rem] font-bebas tracking-[0.18em] uppercase ${color}`}>
      {value || "—"}
    </span>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Phase 24-32 Step 7 — AI Compliance Dashboard tab
// Inventory-style product cards + AI scan envelope + approve/reject/rescan
// ───────────────────────────────────────────────────────────────────────────
function ComplianceTab() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [detailHandle, setDetailHandle] = useState(null);
  const [detailLog, setDetailLog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/admin/ai-compliance/queue", { params: { limit: 200 } });
      setQueue(data.items || []);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "all") return queue;
    return queue.filter((p) => (p.compliance_status || "pending_approval") === filter);
  }, [queue, filter]);

  const counts = useMemo(() => {
    const acc = { all: queue.length };
    for (const p of queue) {
      const k = p.compliance_status || "pending_approval";
      acc[k] = (acc[k] || 0) + 1;
    }
    return acc;
  }, [queue]);

  const onScan = async (handle) => {
    setBusyId(handle);
    try {
      await api.post("/admin/ai-compliance/scan", { handle });
      await load();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusyId(null); }
  };

  const onApprove = async (handle) => {
    setBusyId(handle);
    try {
      await api.patch(`/admin/inventory/products/${handle}`, {
        approved: true, compliance_status: "approved",
      });
      await load();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusyId(null); }
  };

  const onReject = async (handle) => {
    if (!confirm("Reject this product? It will not appear publicly.")) return;
    setBusyId(handle);
    try {
      await api.patch(`/admin/inventory/products/${handle}`, {
        approved: false, compliance_status: "rejected",
      });
      await load();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally { setBusyId(null); }
  };

  const openDetail = async (handle) => {
    setDetailHandle(handle); setDetailLog(null);
    try {
      const { data } = await api.get(`/admin/ai-compliance/log/${handle}`);
      setDetailLog((data.items || [])[0] || null);
    } catch { /* ignore */ }
  };

  const FILTERS = [
    { id: "all", label: "All" },
    { id: "pending_approval", label: "Pending Approval" },
    { id: "ai_review_passed", label: "AI Passed" },
    { id: "pending_review", label: "Pending Review" },
    { id: "missing_data", label: "Missing Data" },
    { id: "rejected", label: "Rejected" },
    { id: "approved", label: "Approved" },
  ];

  return (
    <section data-testid="tab-compliance-content" className="space-y-4">
      {err && (
        <div className="glass-panel p-3 border border-rose-500/30 text-rose-200 text-sm">{err}</div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="compliance-filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full border text-xs font-bebas tracking-[0.18em] uppercase ${
              filter === f.id ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40"
                              : "border-white/10 text-lifted-dim hover:text-white"}`}
            data-testid={`compliance-filter-${f.id}`}
          >
            {f.label} <span className="opacity-50">({counts[f.id] || 0})</span>
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto px-3 py-1.5 rounded text-xs text-lifted-dim hover:text-white border border-white/10"
          data-testid="compliance-refresh"
        >
          <RefreshCw className="inline h-3 w-3 mr-1" /> Refresh
        </button>
        <ShopifyMergeButton onMergeComplete={load} />
      </div>

      {loading ? (
        <div className="text-center text-lifted-dim py-10" data-testid="compliance-loading">
          <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading queue…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-lifted-dim py-10" data-testid="compliance-empty">
          No products in this bucket. Upload a product to trigger AI scan.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
             data-testid="compliance-grid">
          {filtered.map((p) => (
            <article
              key={p.handle}
              data-testid={`compliance-card-${p.handle}`}
              className="rounded-xl border border-white/10 bg-black/40 overflow-hidden flex flex-col"
            >
              <div className="aspect-square bg-black/60 relative overflow-hidden">
                <ProductImg product={p} alt={p.title} className="w-full h-full object-cover" data-testid={`compliance-card-${p.handle}-img`} />
                <ComplianceStatusBadge status={p.compliance_status} />
              </div>
              <div className="p-3 flex flex-col gap-1 flex-1">
                <h3 className="text-sm text-white font-bebas tracking-wide uppercase line-clamp-2 min-h-[2.5em]">
                  {p.title || p.handle}
                </h3>
                <div className="text-[0.65rem] text-lifted-dim font-mono truncate">
                  {p.owner_type || "—"} · {p.category || "—"}
                </div>
                <div className="text-[0.65rem] text-lifted-dim font-mono truncate">
                  Type: {p.product_type || "—"}
                </div>
                {p.compliance_decision && (
                  <div className="text-[0.6rem] text-amber-200 mt-1">
                    {p.compliance_decision.replace(/_/g, " ")}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1 mt-3">
                  <button
                    onClick={() => openDetail(p.handle)}
                    className="px-2 py-1 text-[0.65rem] uppercase tracking-wider rounded border border-white/10 text-lifted-dim hover:text-white"
                    data-testid={`compliance-detail-${p.handle}`}
                  >
                    Detail
                  </button>
                  <button
                    onClick={() => onScan(p.handle)}
                    disabled={busyId === p.handle}
                    className="px-2 py-1 text-[0.65rem] uppercase tracking-wider rounded border border-sky-400/30 text-sky-200 hover:bg-sky-500/15"
                    data-testid={`compliance-rescan-${p.handle}`}
                  >
                    {busyId === p.handle ? "…" : "Rescan"}
                  </button>
                  <button
                    onClick={() => onApprove(p.handle)}
                    disabled={busyId === p.handle}
                    className="px-2 py-1 text-[0.65rem] uppercase tracking-wider rounded bg-lifted-green/15 text-lifted-green border border-lifted-green/40 hover:bg-lifted-green/25"
                    data-testid={`compliance-approve-${p.handle}`}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onReject(p.handle)}
                    disabled={busyId === p.handle}
                    className="px-2 py-1 text-[0.65rem] uppercase tracking-wider rounded bg-rose-500/15 text-rose-200 border border-rose-500/40 hover:bg-rose-500/25"
                    data-testid={`compliance-reject-${p.handle}`}
                  >
                    Reject
                  </button>
                </div>
                <Link
                  to={`/admin/inventory/${p.handle}`}
                  className="mt-1 text-[0.6rem] text-center text-lifted-dim hover:text-lifted-green flex items-center justify-center gap-1"
                  data-testid={`compliance-edit-${p.handle}`}
                >
                  <Edit3 className="h-3 w-3" /> Edit details
                </Link>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {detailHandle && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="compliance-detail-drawer">
          <div className="glass-panel max-w-2xl w-full max-h-[80vh] overflow-y-auto p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bebas text-lifted-green text-lg tracking-[0.18em] uppercase">
                AI Compliance Log
              </h3>
              <button onClick={() => setDetailHandle(null)} className="text-white/50 hover:text-white">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="font-mono text-xs text-lifted-dim">{detailHandle}</div>
            {!detailLog ? (
              <p className="text-lifted-dim text-sm">No scan log found. Click Rescan to run AI compliance.</p>
            ) : (
              <div className="space-y-3 text-xs">
                <div><b className="text-lifted-green">Decision:</b> {detailLog.result?.decision}</div>
                <div><b className="text-lifted-green">Status:</b> {detailLog.result?.status}</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <ScanCard title="Heuristic" data={detailLog.result?.heuristic} />
                  <ScanCard title="Claude" data={detailLog.result?.claude} />
                  <ScanCard title="ChatGPT" data={detailLog.result?.gpt} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function ComplianceStatusBadge({ status }) {
  const cfg = {
    approved:        ["bg-lifted-green/20 text-lifted-green border-lifted-green/50", "Approved"],
    ai_review_passed:["bg-sky-500/15 text-sky-200 border-sky-400/40", "AI Passed"],
    pending_review:  ["bg-amber-400/15 text-amber-200 border-amber-400/40", "Pending Review"],
    pending_approval:["bg-amber-400/15 text-amber-200 border-amber-400/40", "Pending"],
    missing_data:    ["bg-rose-500/15 text-rose-200 border-rose-500/40", "Missing Data"],
    rejected:        ["bg-rose-500/15 text-rose-200 border-rose-500/40", "Rejected"],
  }[status] || ["bg-white/5 text-white/60 border-white/10", status || "—"];
  return (
    <span className={`absolute top-2 left-2 text-[0.6rem] tracking-[0.18em] font-bebas uppercase border rounded-full px-2 py-0.5 ${cfg[0]}`}>
      {cfg[1]}
    </span>
  );
}

function ScanCard({ title, data }) {
  if (!data) return (
    <div className="border border-white/10 rounded p-2">
      <div className="font-bebas tracking-wider text-lifted-dim text-[0.6rem]">{title}</div>
      <div className="text-lifted-dim text-[0.65rem] mt-1">Not run</div>
    </div>
  );
  const verdict = data.verdict || (data.passed ? "pass" : "flag");
  const tone = verdict === "pass" ? "text-lifted-green" :
                verdict === "reject" ? "text-rose-300" : "text-amber-200";
  return (
    <div className="border border-white/10 rounded p-2">
      <div className="font-bebas tracking-wider text-lifted-dim text-[0.6rem]">{title}</div>
      <div className={`text-xs font-mono uppercase ${tone}`}>{verdict}</div>
      {data.risk_level && <div className="text-[0.65rem] text-lifted-dim mt-1">Risk: {data.risk_level}</div>}
      {(data.reasons || data.issues || []).slice(0, 3).map((r, i) => (
        <div key={i} className="text-[0.6rem] text-white/70 truncate">• {r}</div>
      ))}
    </div>
  );
}
